import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import Api from 'api';
import { SimpleModal } from 'components/Modal';
import { StyledButton } from 'components/Buttons/DefaultButton/DefaultButton.styles';
import MaterialInput from 'components/Inputs/MaterialInput';
import useInvoiceInformation from 'hooks/useInvoiceInformation';
import { validPaymentEmail } from 'utils/talentAddress';

export default function TalentInvoiceInformation({ invoiceId, initiallyOpen = false, onClose, settled = false }: {
  invoiceId: number; initiallyOpen?: boolean; onClose?: () => void; settled?: boolean;
}) {
  const { data, pending, error, refresh, mutate } = useInvoiceInformation(invoiceId);
  const [open, setOpen] = useState(initiallyOpen);
  const [paymentEmail, setPaymentEmail] = useState('');
  const [validation, setValidation] = useState('');
  const request = settled ? null : data?.request;
  useEffect(() => { setOpen(initiallyOpen); setPaymentEmail(''); setValidation(''); }, [invoiceId, initiallyOpen]);
  useEffect(() => { setPaymentEmail(request?.savedPaymentEmail || ''); setValidation(''); }, [request?.requestId, request?.generation, request?.savedPaymentEmail]);
  const close = () => { if (!pending) { setOpen(false); onClose?.(); } };
  const send = async () => {
    if (!request || request.status !== 'awaiting_response') return;
    if (!validPaymentEmail(paymentEmail)) { setValidation('Enter a valid payment email of 100 characters or fewer.'); return; }
    const receipt = await mutate(() => Api.shareInvoiceInformation(invoiceId, request.generation, paymentEmail.trim()));
    if (receipt?.accepted) setOpen(false);
  };
  const contact = async () => {
    if (!request || request.item !== 'banking_contact') return;
    const receipt = await mutate(() => Api.acknowledgeInvoiceInformation(invoiceId, request.generation));
    if (receipt?.acknowledged) setOpen(false);
  };
  return <section data-testid="talent-invoice-information">
    <StyledButton variant="text" onClick={() => setOpen(true)} data-testid="talent-information-open">Payment information and sharing history</StyledButton>
    <SimpleModal isOpen={open}>
      <h2>Payment information</h2>
      {error && <p role="alert">{error}</p>}
      {!data && <StyledButton onClick={refresh} disabled={pending} data-testid="talent-information-refresh">{pending ? 'Checking status…' : 'Refresh status'}</StyledButton>}
      {request?.status === 'awaiting_response' && <>
        <p>{data?.practiceName} needs your {data?.paymentEmailLabel.toLowerCase()} to pay you for the shift on {dayjs(data?.shiftDate).format('MMM D, YYYY')}. Do you give us permission to send it to them?</p>
        <MaterialInput id="talent-information-email" name="paymentEmail" type="email" label={data?.paymentEmailLabel}
          value={paymentEmail} disabled={pending} onChange={event => { setPaymentEmail(event.target.value); setValidation(''); }}
          error={!!validation} helperText={validation || 'For this request only. Your saved profile will stay the same.'}
          inputProps={{inputProps:{'data-testid':'talent-information-email',maxLength:100}}} />
        <StyledButton variant="contained" disabled={pending || !paymentEmail.trim()} onClick={send} data-testid="talent-information-send">Send to office</StyledButton>
        <StyledButton variant="text" disabled={pending} onClick={close} data-testid="talent-information-cancel">Cancel</StyledButton>
      </>}
      {request?.status === 'contact_requested' && !request.acknowledgedOn && <>
        <p>{data?.practiceName} needs your direct deposit details. TempStars doesn't store your banking info — please call the office directly to provide it. Don't send banking details over email or chat.</p>
        <StyledButton variant="contained" onClick={contact} disabled={pending} data-testid="talent-information-contact-okay">OKAY</StyledButton>
      </>}
      {data && (!request || request.status === 'shared' || request.status === 'expired' || request.acknowledgedOn) && <p>No payment information is waiting for your response.</p>}
      {!!data?.history?.length && <div data-testid="talent-information-history"><h3>Sharing history</h3>{data.history.map(entry =>
        <p key={entry.id}>{data.practiceName}: {data.paymentEmailLabel} — {entry.paymentEmail}, shared {dayjs(entry.sharedOn).format('MMM D, YYYY, h:mm A')}.</p>)}</div>}
      {request?.status !== 'awaiting_response' && <StyledButton variant="text" disabled={pending} onClick={close}>Close</StyledButton>}
    </SimpleModal>
  </section>;
}
